import type { Company, ReferentMutation } from '../@types/Company';

/**
 * Get all valid referents (with complete basic data: title, firstname, lastname)
 */
/**
 * When saving branding referents from the wizard draft, preserve per-referent
 * report PDF paths already stored on the company (not part of the draft form).
 */
export const mergeReferentsPreservingPdfIntroduction = (
  incoming: ReferentMutation[],
  existing: ReferentMutation[] | undefined,
): ReferentMutation[] => {
  if (!existing?.length) {
    return incoming;
  }

  const existingById = new Map(existing.map((referent) => [referent.id, referent]));

  return incoming.map((referent) => {
    const previous = existingById.get(referent.id);
    if (!previous?.pdfIntroductionName?.trim()) {
      return referent;
    }
    if (referent.pdfIntroductionName?.trim()) {
      return referent;
    }
    return { ...referent, pdfIntroductionName: previous.pdfIntroductionName };
  });
};

export const getValidReferents = (company?: Company | null): ReferentMutation[] => {
  if (!company?.brandingSettings) {return [];}
  
  const { brandingSettings } = company;
  
  // Return referents array filtered to only include valid entries
  if (brandingSettings.referents && brandingSettings.referents.length > 0) {
    return brandingSettings.referents.filter(r => 
      r.title?.trim() && r.firstname?.trim() && r.lastname?.trim(),
    );
  }
  
  return [];
};

export const isCompanyBrandingComplete = (company?: Company | null): boolean => {
  if (!company) {
    return false;
  }

  const { brandingSettings } = company;

  if (!brandingSettings) {
    return false;
  }

  const { color1, color2, color3 } = brandingSettings;

  const hasColors = Boolean(color1?.trim())
    && Boolean(color2?.trim())
    && Boolean(color3?.trim());

  const hasLogo = Boolean(brandingSettings.brandingImageName?.trim());
  const hasCommercialName = Boolean(brandingSettings.commercialName?.trim());
  const hasTemplate = Boolean(brandingSettings.defaultTemplate);

  // At least one referent with title, firstname and lastname (photo is optional)
  const hasReferent = getValidReferents(company).length > 0;

  return hasCommercialName && hasLogo && hasColors && hasTemplate && hasReferent;
};

const HEX6 = /^#([0-9a-fA-F]{6})$/;

/**
 * Wizard / routing: optional steps (no prerequisite for later steps):
 * - Nom commercial (0)
 * - Logo (1) — matches LogoStep always-valid behaviour
 * Referent photo is optional for step 3; civilité + noms are required.
 */

/** Saved branding colors are complete (wizard step 5). */
export function areCompanyBrandingColorsPersisted(company?: Company | null): boolean {
  const c = company?.brandingSettings;
  if (!c) {
    return false;
  }
  return HEX6.test(c.color1 || '') && HEX6.test(c.color2 || '') && HEX6.test(c.color3 || '');
}

/**
 * Colors are satisfied for routing / récap when either saved as valid hex
 * or all three are still blank (assistant applies default palette until save).
 */
export function areCompanyBrandingColorsPersistedOrDefault(company?: Company | null): boolean {
  if (areCompanyBrandingColorsPersisted(company)) {
    return true;
  }
  const c = company?.brandingSettings;
  if (!c) {
    return true;
  }
  const v1 = (c.color1 || '').trim();
  const v2 = (c.color2 || '').trim();
  const v3 = (c.color3 || '').trim();
  const anySet = v1.length > 0 || v2.length > 0 || v3.length > 0;
  if (anySet) {
    return false;
  }
  return true;
}

/**
 * First wizard step index (0–7) that still needs work from persisted company data.
 * Skips optional steps (0–1) unless nothing else is missing.
 * Step 6 (report upload) is optional and always skipped here.
 * 7 = all content steps done → récapitulatif.
 */
export function firstIncompleteBrandingWizardStep(company?: Company | null): number {
  if (!company?.id) {
    return 0;
  }
  const emailSub = (company.brandingSettings?.emailSubcontractorName || '').trim();
  if (emailSub.length === 0) {
    return 2;
  }
  if (getValidReferents(company).length === 0) {
    return 3;
  }
  if (!company.brandingSettings?.defaultTemplate) {
    return 4;
  }
  if (!areCompanyBrandingColorsPersistedOrDefault(company)) {
    return 5;
  }
  return 7;
}

/**
 * Whether the user may open wizard step `targetStep` (0–7) given saved data.
 * Steps 0–1 are optional (always reachable). Step 6 (report upload) is optional.
 * Later steps only require prior *required* fields.
 */
export function canAccessBrandingWizardStep(company: Company | null | undefined, targetStep: number): boolean {
  if (targetStep < 0 || targetStep > 7) {
    return false;
  }
  if (targetStep <= 1) {
    return true;
  }
  const emailSub = (company?.brandingSettings?.emailSubcontractorName || '').trim();
  const hasReferent = company ? getValidReferents(company).length > 0 : false;
  const hasTemplate = Boolean(company?.brandingSettings?.defaultTemplate);
  if (targetStep === 2) {
    return true;
  }
  if (targetStep === 3) {
    return emailSub.length > 0;
  }
  if (targetStep === 4) {
    return emailSub.length > 0 && hasReferent;
  }
  if (targetStep === 5) {
    return emailSub.length > 0 && hasReferent && hasTemplate;
  }
  // Steps 6 (report upload, optional) and 7 (summary) require all prior required steps
  return emailSub.length > 0 && hasReferent && hasTemplate && areCompanyBrandingColorsPersistedOrDefault(company);
}

export default isCompanyBrandingComplete;
